import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Scale, ArrowRight, BookOpen, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from '../config/branding';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Connexion réussie');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        {/* Background image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1668511949286-bee319d2271f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/95 via-[#0F172A]/85 to-[#0F172A]/90" />
        
        {/* Content */}
        <div className="relative z-10 flex items-center justify-between">
          {/* Logo - NO BACKGROUND */}
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt={BRAND.name} className="w-10 h-10" />
            <span className="text-2xl font-heading font-semibold tracking-tight text-[#F8FAFC]">
              {BRAND.name}
            </span>
          </div>
          {/* Liens navigation */}
          <div className="flex items-center gap-6">
            <Link 
              to="/features" 
              className="text-sm text-[#94A3B8] hover:text-[#F8FAFC] transition-colors font-medium"
            >
              Fonctionnalités
            </Link>
            <Link 
              to="/pricing" 
              className="text-sm text-[#B69D74] hover:text-[#D4B896] transition-colors font-medium"
            >
              Tarifs
            </Link>
          </div>
        </div>
        
        <div className="relative z-10 max-w-lg">
          <h1 className="text-4xl lg:text-5xl font-heading font-semibold tracking-tight leading-tight mb-6 text-[#F8FAFC]">
            L'excellence juridique,
            <br />
            <span className="text-[#B69D74]">augmentée par l'IA</span>
          </h1>
          <p className="text-lg text-[#94A3B8] leading-relaxed mb-8">
            Recherche jurisprudentielle, analyse de dossiers, gestion d'affaires. 
            Les outils dont vous avez besoin pour exercer avec précision.
          </p>
          
          {/* Feature list */}
          <div className="space-y-4">
            {[
              { icon: BookOpen, text: 'Accès aux bases juridiques officielles' },
              { icon: Scale, text: 'Analyse prédictive des dossiers' },
              { icon: Building2, text: 'Gestion complète du cabinet' },
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-4 animate-slide-up" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="w-px h-4 bg-[#B69D74]" />
                <feature.icon className="w-4 h-4 text-[#B69D74]" strokeWidth={1.5} />
                <span className="text-sm text-[#94A3B8]">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Official sources footer */}
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-px bg-[#B69D74]/50" />
            <span className="text-[10px] text-[#94A3B8]/60 uppercase tracking-widest">
              Données officielles
            </span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {['Légifrance', 'Judilibre', 'EUR-Lex', 'CEDH'].map((source) => (
              <span key={source} className="text-xs text-[#94A3B8]/50">{source}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-8 sm:p-8 lg:p-16 bg-[#0F172A]">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo - NO BACKGROUND */}
          <div className="flex items-center justify-center gap-3 lg:hidden mb-8 sm:mb-12">
            <img src="/logo.png" alt={BRAND.name} className="w-10 h-10" />
            <span className="text-2xl font-heading font-semibold text-[#F8FAFC]">{BRAND.name}</span>
          </div>
          
          <div className="mb-8 sm:mb-10 text-center lg:text-left">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#B69D74] mb-3">
              Espace avocat
            </p>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-[#F8FAFC] mb-2">
              Connexion
            </h2>
            <p className="text-[#94A3B8]">
              Accédez à votre tableau de bord
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                Adresse email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="avocat@cabinet.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="h-12 sm:h-12 bg-[#0F172A] border-[#B69D74]/30 text-[#F8FAFC] text-base placeholder:text-[#94A3B8]/50 focus:border-[#B69D74] focus:ring-1 focus:ring-[#B69D74]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-[#94A3B8]">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="h-12 sm:h-12 bg-[#0F172A] border-[#B69D74]/30 text-[#F8FAFC] text-base placeholder:text-[#94A3B8]/50 focus:border-[#B69D74] focus:ring-1 focus:ring-[#B69D74]"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-14 sm:h-12 bg-[#B69D74] text-[#0F172A] font-bold uppercase tracking-wider hover:bg-[#C5AE87] active:bg-[#A88D64] transition-all duration-300 gap-2 text-base sm:text-sm"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="spinner" />
              ) : (
                <>
                  Accéder à mon espace
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-8 pt-8 border-t border-[#B69D74]/10">
            <p className="text-center text-sm text-[#94A3B8]">
              Pas encore de compte ?{' '}
              <Link 
                to="/register" 
                className="text-[#B69D74] hover:text-[#C5AE87] font-medium transition-colors"
                data-testid="register-link"
              >
                Créer un compte
              </Link>
            </p>
            <p className="text-center text-sm text-[#94A3B8] mt-4">
              <Link 
                to="/pricing" 
                className="text-[#B69D74] hover:text-[#C5AE87] font-medium transition-colors inline-flex items-center gap-1"
              >
                Voir nos offres et tarifs
                <ArrowRight className="w-3 h-3" />
              </Link>
            </p>
          </div>
          
          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-[10px] text-[#94A3B8]/40 uppercase tracking-wider">
              Sécurisé · Confidentiel · Conforme RGPD
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
